import { ChangeEvent, useEffect, useRef, useState } from "react"
import { DateRange } from "react-day-picker"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { useOutletContext } from "react-router"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { startOfDay } from "date-fns"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Button } from "@/components/ui/button"
import { DateRangeInput } from "@/components/ui/date-range-input"
import { DeprecatedConfirmationDialog } from "@/components/ui/deprecated-confirmation-dialog"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { toast } from "@/hooks/useToast"
import { DateOnly } from "@/lib/date-only"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { chSickLeaveFragment$key } from "@/modules/patients/detail/sick-leave-certificate/ch/services/__generated__/chSickLeaveFragment.graphql"
import {
  SickLeaveCertificateSchema,
  SickLeaveCertificateType,
} from "@/modules/patients/detail/sick-leave-certificate/de/schema/sick-leave-certificate-schema"
import { deSickLeaveFragment$key } from "@/modules/patients/detail/sick-leave-certificate/de/services/__generated__/deSickLeaveFragment.graphql"
import { CertificateType } from "@/modules/patients/detail/sick-leave-certificate/de/services/__generated__/useDeCreateSickLeaveMutation.graphql"
import { deSickLeaveFragment } from "@/modules/patients/detail/sick-leave-certificate/de/services/deSickLeaveFragment"
import { useDeCreateSickLeaveMutation } from "@/modules/patients/detail/sick-leave-certificate/de/services/useDeCreateSickLeaveMutation"
import { useDeDeleteSickLeaveMutation } from "@/modules/patients/detail/sick-leave-certificate/de/services/useDeDeleteSickLeaveMutation"
import { useDeGenerateSickLeaveDocumentMutation } from "@/modules/patients/detail/sick-leave-certificate/de/services/useDeGenerateSickLeaveDocumentMutation"
import { useDeUpdateSickLeaveMutation } from "@/modules/patients/detail/sick-leave-certificate/de/services/useDeUpdateSickLeaveMutation"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { dePatientByIdQuery$data } from "@/modules/patients/services/de/__generated__/dePatientByIdQuery.graphql"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { ArrElement } from "@/types/ArrElement"

const CERTIFICATE_TYPE: Record<CertificateType, CertificateType> = {
  FIRST: "FIRST",
  FOLLOW_UP: "FOLLOW_UP",
}

export interface PatientInformationProps {
  patient: PatientFragment$data
  insuree: ArrElement<NonNullable<dePatientByIdQuery$data["patientById"]>["insurees"]>
}

type DeSickLeaveCertificateDrawerProps = {
  progressNoteId: string
  sickLeaveRef?: deSickLeaveFragment$key | chSickLeaveFragment$key
}

const DeSickLeaveCertificateDrawer = withErrorBoundary(
  ({ progressNoteId, sickLeaveRef }: DeSickLeaveCertificateDrawerProps) => {
    const { t } = useTranslation()
    const closeDrawer = useDrawerStore((state) => state.closeDrawer)
    const today = startOfDay(new Date())
    const [durationValue, setDurationValue] = useState<DateRange>()
    const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false)
    const dateRangeInputRef = useRef<HTMLInputElement>(null)
    const [createSickLeave, isCreating] = useDeCreateSickLeaveMutation()
    const [updateSickLeave, isUpdating] = useDeUpdateSickLeaveMutation()
    const [deleteSickLeave] = useDeDeleteSickLeaveMutation()
    const [generateSickLeaveDocument, isGenerating] = useDeGenerateSickLeaveDocumentMutation()
    const { patient } = useOutletContext<PatientInformationProps>()

    const patientId = usePatientParam()
    const sickLeaveData = useFragment(deSickLeaveFragment, sickLeaveRef as deSickLeaveFragment$key)
    const updatedAt = sickLeaveData?.updatedAt || sickLeaveData?.createdAt
    const updatedBy = sickLeaveData?.updatedBy || sickLeaveData?.createdBy
    const isEditMode = sickLeaveData?.id
    const isAddMode = !isEditMode && patientId && progressNoteId

    const existingData = sickLeaveData
      ? {
          typeOfCertificate: sickLeaveData.certificateType,
          timeFrame: { from: new Date(sickLeaveData.from), to: new Date(sickLeaveData.to) },
        }
      : null

    const form = useForm<SickLeaveCertificateType>({
      resolver: zodResolver(SickLeaveCertificateSchema),
      defaultValues: existingData ?? {
        typeOfCertificate: "FIRST",
        timeFrame: { from: today },
      },
    })

    const {
      setValue,
      trigger,
      formState: { errors, isSubmitted, isDirty },
    } = form

    useDirtyDrawerMaker(isDirty)

    useEffect(() => {
      if (!sickLeaveRef) {
        setTimeout(() => {
          dateRangeInputRef.current?.focus()
        }, 200)
      }
    }, [sickLeaveRef])

    useEffect(() => {
      if (sickLeaveData) {
        const { from, to } = sickLeaveData
        setDurationValue({ from: new Date(from), to: new Date(to) })
      }
    }, [sickLeaveData])

    const handleChange = (range?: DateRange) => {
      if (range?.from) {
        setValue(
          "timeFrame",
          { from: range.from, to: range.to },
          {
            shouldValidate: isSubmitted,
            shouldDirty: true,
          },
        )
        if (range.to) {
          trigger("timeFrame")
        }
      }
    }

    const handleInputChange = (_e: ChangeEvent<HTMLInputElement>, range?: DateRange) => {
      const selectedRange = { from: range?.from ?? today, to: range?.to }
      setValue("timeFrame", selectedRange, { shouldDirty: true })
      if (selectedRange.to) {
        trigger("timeFrame")
      }
    }

    const handleGenerateSickLeaveDocument = (sickLeaveId: string) => {
      if (patient) {
        generateSickLeaveDocument({
          params: {
            sickLeaveId,
          },
          onCompleted() {
            handleCloseDrawer()
          },
        })
      }
    }

    const handleSubmit = (data: SickLeaveCertificateType) => {
      const { timeFrame, typeOfCertificate } = data
      if (!timeFrame.to || !progressNoteId) return

      const formattedData = {
        certificateType: typeOfCertificate,
        from: new DateOnly(timeFrame.from).toString(),
        to: new DateOnly(timeFrame.to).toString(),
      }

      if (isEditMode) {
        updateSickLeave({
          variables: {
            input: {
              certificateType: formattedData.certificateType,
              from: formattedData.from,
              to: formattedData.to,
              id: sickLeaveData.id,
            },
          },
          onCompleted(response) {
            const sickLeaveId = response.de_updateSickLeave?.de_SickLeave?.id
            if (sickLeaveId) {
              handleGenerateSickLeaveDocument(sickLeaveId)
            }
          },
        })
      }

      if (isAddMode) {
        createSickLeave({
          variables: {
            input: {
              certificateType: formattedData.certificateType,
              from: formattedData.from,
              to: formattedData.to,
              patientId: patientId,
              progressNoteId,
            },
          },
          onCompleted(response) {
            const sickLeaveId = response.de_createSickLeave?.de_SickLeave?.id
            if (sickLeaveId) handleGenerateSickLeaveDocument(sickLeaveId)
          },
          updater(store, data) {
            try {
              const progressNoteRecordProxy = store.get(progressNoteId)
              const sickLeave = data?.de_createSickLeave?.de_SickLeave

              if (sickLeave?.id) {
                const newRecordProxy = store.get(sickLeave.id)
                if (newRecordProxy) {
                  progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "de_sickLeave")
                }
              }
            } catch (error) {
              console.error("Error while processing data:", error)
            }
          },
        })
      }
    }

    const onCertificateTypeChange = (value: CertificateType) => {
      setValue("typeOfCertificate", value, { shouldDirty: true })
    }

    const handleCloseDrawer = () => {
      closeDrawer()
      FaroInsightsWrapper.pushEvent("documents_sickLeave_close")
    }

    const handleOpenConfirmDialog = (isOpen: boolean) => {
      setIsOpenConfirmDialog(isOpen)
    }

    const handleDelete = () => {
      if (progressNoteId && sickLeaveData) {
        deleteSickLeave({
          variables: {
            input: {
              id: sickLeaveData.id,
            },
          },
          onCompleted() {
            handleCloseDrawer()
            handleOpenConfirmDialog(false)
            toast({
              description: t("patient.sickLeaveCertificate.deleteMessage.success.de"),
            })
          },
        })
      }
    }

    return (
      <Drawer open onOpenChange={handleCloseDrawer}>
        <FormProvider {...form}>
          <DrawerContent>
            <DrawerClose className="top-2 right-2" />
            <DrawerHeader className="mb-4 py-4.5 pr-4" data-testid="sick-leave-header">
              <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
                {t("episodeOfCare.progressNote.sickLeaveCertificate.de")}
              </DrawerTitle>
            </DrawerHeader>
            <DrawerBody className="flex gap-4 pt-0.5" data-testid="sick-leave-body">
              <DateRangeInput
                label={t("patient.sickLeaveCertificate.timeFrame")}
                value={durationValue}
                onSelectDays={handleChange}
                onInputChange={handleInputChange}
                error={errors?.timeFrame?.from?.message ?? errors?.timeFrame?.to?.message}
                translateError
                required
                data-testid="date-range-input"
                ref={dateRangeInputRef}
              />
              <div className="grid gap-2">
                <p className="text-on-surface-subdued text-sm">{t("patient.sickLeaveCertificate.typeOfCertificate")}</p>
                <RadioGroup
                  className="flex flex-wrap"
                  onValueChange={onCertificateTypeChange}
                  defaultValue={form.getValues("typeOfCertificate")}>
                  <RadioGroupItem value={CERTIFICATE_TYPE.FIRST} data-testid="firstCertificate-radio-item">
                    <RadioGroupLabel>{t("patient.sickLeaveCertificate.firstCertificate")}</RadioGroupLabel>
                  </RadioGroupItem>
                  <RadioGroupItem value={CERTIFICATE_TYPE.FOLLOW_UP} data-testid="followUpCertificate-radio-item">
                    <RadioGroupLabel>{t("patient.sickLeaveCertificate.followUpCertificate")}</RadioGroupLabel>
                  </RadioGroupItem>
                </RadioGroup>
              </div>
            </DrawerBody>
            <DrawerFooter className="flex-wrap gap-2" data-testid="sick-leave-footer">
              {sickLeaveData && (
                <div className="flex gap-2">
                  {updatedAt && updatedBy && (
                    <LastModificationInfo
                      updatedAt={updatedAt}
                      updatedBy={updatedBy}
                      data-testid="sick-leave-last-modification"
                    />
                  )}
                  <button
                    onClick={() => {
                      FaroInsightsWrapper.pushEvent("documents_sickLeave_delete")
                      handleOpenConfirmDialog(true)
                    }}
                    data-testid="de-sick-leave-delete-button">
                    <TrashSimple className="text-on-surface-subdued" />
                  </button>
                </div>
              )}
              <div className="ml-auto flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    handleCloseDrawer()
                  }}
                  data-testid="sick-leave-close-button">
                  {t("common.button.close")}
                </Button>
                <Button
                  className="min-w-16.25"
                  onClick={() => {
                    FaroInsightsWrapper.pushEvent("documents_sickLeave_save")
                    form.handleSubmit(handleSubmit)()
                  }}
                  disabled={sickLeaveData && !isDirty}
                  data-testid="sick-leave-save-button"
                  isLoading={isCreating || isUpdating || isGenerating}>
                  {t("common.button.save")}
                </Button>
              </div>
            </DrawerFooter>
          </DrawerContent>
        </FormProvider>

        <DeprecatedConfirmationDialog
          title={t("patient.sickLeaveCertificate.deleteConfirmTitle.de")}
          content={t("episodeOfCare.deleteProgressNote.content")}
          open={isOpenConfirmDialog}
          onConfirm={handleDelete}
          confirmVariant="danger"
          onOpenChange={handleOpenConfirmDialog}
        />
      </Drawer>
    )
  },
  "DeSickLeaveCertificateDrawer",
)

export { DeSickLeaveCertificateDrawer }
